// Firebase Database Schema Documentation
// This file documents the new multi-user structure (Phase 0+)
// 
// IMPORTANT: Old "sessions/" structure remains intact for backward compatibility
// New structure runs in parallel during migration

import { Logger } from '../utils/logger';

const log = Logger.create('FirebaseSchema');

export const FIREBASE_SCHEMA = {
  // ============================================
  // LEGACY STRUCTURE (Keep for compatibility)
  // ============================================
  sessions: {
    description: "Legacy quiz sessions (pre-multi-user)",
    path: "sessions/{sessionId}",
    structure: {
      questions: "array",
      settings: "object",
      participants: "object",
      answers: "object",
      currentQuestion: "number",
      showResults: "boolean",
      // ... existing fields
    },
    migration: {
      willBecome: "quiz_instances/{userId}/{instanceId}",
      addedField: "userId"  // Added to existing sessions for ownership
    }
  },

  // ============================================
  // NEW MULTI-USER STRUCTURE
  // ============================================
  
  // User profiles
  users: {
    description: "User metadata and preferences",
    path: "users/{userId}",
    structure: {
      email: "string",
      displayName: "string",
      createdAt: "timestamp",
      lastLogin: "timestamp",
      settings: {
        theme: "light|dark",
        defaultCourse: "courseId|null"
      }
    },
    security: "Owner only (read/write: userId === auth.uid)"
  },

  // Courses/Projects
  courses: {
    description: "Organize templates by course/project",
    path: "courses/{userId}/{courseId}",
    structure: {
      name: "string",
      description: "string",
      color: "string (hex)",
      icon: "string (emoji)",
      createdAt: "timestamp",
      archived: "boolean"
    },
    security: "Owner only",
    example: {
      name: "Computer Science 101",
      description: "Introduction to Programming",
      color: "#3B82F6",
      icon: "💻",
      createdAt: 1234567890,
      archived: false
    }
  },

  // Quiz Templates (reusable blueprints)
  quiz_templates: {
    description: "Reusable quiz blueprints with questions and settings",
    path: "quiz_templates/{userId}/{templateId}",
    structure: {
      name: "string",
      courseId: "string|null",  // Optional course assignment
      description: "string",
      tags: "string[]",
      questions: [
        {
          question: "string",
          options: "string[]",
          correctAnswerIndex: "number",
          correctAnswerIndices: "number[] (for multiple correct)",
          allowMultiple: "boolean",
          codeBlock: {
            language: "string",
            code: "string"
          },
          terminalOutput: "string"
        }
      ],
      settings: {
        displayName: "string",
        showIntroScreen: "boolean",
        anonymousMode: "boolean",
        studentRegistration: "none|optional|required",  // NEW: Student registration mode
        enableGamification: "boolean",
        basePointsPerQuestion: "number",
        enableTimeBonus: "boolean",
        enableStreakBonus: "boolean",
        streakBonusPoints: "number",
        enableQuestionTimer: "boolean",
        questionTimerSeconds: "number",
        showLeaderboard: "boolean",
        showStreak: "boolean",
        lockOnReveal: "boolean",
        enableJokers: "boolean",
        enableJoker5050: "boolean",
        enableJokerAudience: "boolean",
        enableJokerBestPlayer: "boolean",
        enableJokerTime: "boolean"
      },
      // NEW: Student visibility and practice settings
      studentAccess: {
        visibility: "visible|hidden|after_completion",  // visible to students in course
        allowPractice: "boolean",                       // students can practice/repeat
        practiceAfterSession: "boolean",                // only allow practice after live session
        showCorrectAnswers: "never|after_submit|after_quiz",  // when to show correct answers
        showExplanations: "boolean"                     // show explanations in practice mode
      },
      createdAt: "timestamp",
      lastModified: "timestamp",
      lastUsed: "timestamp",
      useCount: "number"
    },
    security: "Owner only",
    indexes: ["courseId", "lastUsed", "useCount"]
  },

  // Quiz Instances (live or completed sessions)
  quiz_instances: {
    description: "Live or completed quiz sessions, linked to templates",
    path: "quiz_instances/{userId}/{instanceId}",
    structure: {
      templateId: "string",
      courseId: "string|null",
      name: "string",
      sessionCode: "string (6-char)",
      status: "draft|active|completed|archived",
      questions: "array (copied from template)",
      settings: "object (copied from template)",
      createdAt: "timestamp",
      startedAt: "timestamp|null",
      endedAt: "timestamp|null",
      currentQuestion: "number",
      showResults: "boolean",
      showCorrectAnswer: "boolean",
      answersLocked: "boolean",
      timerState: {
        questionStartedAt: "timestamp",
        timerDurationMs: "number",
        bonusTimeAddedMs: "number",
        questionIndex: "number"
      },
      participants: {
        "{participantId}": {
          name: "string",
          joinedAt: "timestamp",
          totalScore: "number",
          currentStreak: "number",
          // NEW: Student registration linking
          studentId: "string|null",         // Links to students/{studentId} if registered
          isRegistered: "boolean",          // true if linked to student account
          registeredAt: "timestamp|null",   // When they registered (if during quiz)
          email: "string|null"              // Email (for verification tracking)
        }
      },
      answers: {
        "{questionIndex}": {
          "{participantId}": {
            selectedIndex: "number",
            selectedIndices: "number[]",
            submittedAt: "timestamp",
            responseTimeMs: "number",
            isCorrect: "boolean",
            pointsEarned: "number"
          }
        }
      }
    },
    security: {
      owner: "read/write all fields",
      participants: "read quiz data, write own answers"
    },
    indexes: ["templateId", "courseId", "status", "sessionCode"]
  },

  // Session code mapping (for participant access)
  session_codes: {
    description: "Maps 6-character codes to instance IDs",
    path: "session_codes/{sessionCode}",
    structure: {
      userId: "string",
      instanceId: "string",
      createdAt: "timestamp",
      expiresAt: "timestamp|null"
    },
    security: "Public read, owner write",
    purpose: "Allows /quiz/{sessionCode} to find correct instance"
  },

  // ============================================
  // LLM PROMPT TEMPLATES
  // ============================================
  
  // System-wide LLM Prompt Templates (Admin-managed, read by all)
  llm_prompt_templates: {
    description: "Global LLM prompt templates for quiz generation (managed by admin)",
    path: "llm_prompt_templates/{templateId}",
    structure: {
      id: "string",
      name: "string",
      icon: "string (emoji)",
      category: "mint|languages|humanities|arts",
      description: "string",
      systemPrompt: "string (the LLM system prompt)",
      exampleTags: "string[] (example topics)",
      defaultSettings: {
        includeCodeBlocks: "boolean",
        difficulty: "easy|medium|hard"
      },
      createdAt: "timestamp",
      updatedAt: "timestamp",
      isActive: "boolean"
    },
    security: {
      read: "All authenticated users",
      write: "Admin only"
    },
    purpose: "Standard templates shipped with the app, editable by admins"
  },

  // User-specific LLM Template Customizations
  user_llm_templates: {
    description: "User-customized or created LLM prompt templates",
    path: "user_llm_templates/{userId}/{templateId}",
    structure: {
      id: "string",
      name: "string",
      icon: "string (emoji)",
      category: "custom|mint|languages|humanities|arts",
      description: "string",
      systemPrompt: "string (the LLM system prompt)",
      exampleTags: "string[] (example topics)",
      defaultSettings: {
        includeCodeBlocks: "boolean",
        difficulty: "easy|medium|hard"
      },
      baseTemplateId: "string|null (if forked from system template)",
      createdAt: "timestamp",
      updatedAt: "timestamp"
    },
    security: "Owner only (read/write: userId === auth.uid)",
    purpose: "User can fork system templates or create entirely new ones"
  },

  // ============================================
  // STUDENT REGISTRATION SYSTEM
  // ============================================

  // Student profiles (separate from teacher users)
  students: {
    description: "Student user profiles for registered students",
    path: "students/{studentId}",
    structure: {
      email: "string",
      displayName: "string",
      createdAt: "timestamp",
      lastLogin: "timestamp",
      settings: {
        theme: "light|dark"
      },
      enrolledCourses: {
        "{enrollmentId}": {
          courseId: "string",
          teacherUserId: "string",
          enrolledAt: "timestamp",
          status: "active|removed"
        }
      }
    },
    security: "Owner only (read/write: studentId === auth.uid)"
  },

  // Student statistics
  student_stats: {
    description: "Student quiz statistics and progress tracking",
    path: "student_stats/{studentId}",
    structure: {
      totalQuizzesTaken: "number",
      totalQuestionsAnswered: "number",
      totalCorrectAnswers: "number",
      averageScore: "number",
      currentStreak: "number",
      longestStreak: "number",
      lastActivityAt: "timestamp",
      courseStats: {
        "{courseId}": {
          quizzesTaken: "number",
          questionsAnswered: "number",
          correctAnswers: "number",
          averageScore: "number",
          lastQuizAt: "timestamp"
        }
      }
    },
    security: "Owner only for write, course teachers can read"
  },

  // Course invitation codes
  course_invites: {
    description: "Invitation codes for students to join courses",
    path: "course_invites/{inviteCode}",
    structure: {
      teacherUserId: "string",
      courseId: "string",
      createdAt: "timestamp",
      expiresAt: "timestamp|null",
      usageLimit: "number|null (null = unlimited)",
      usageCount: "number",
      isActive: "boolean"
    },
    security: "Public read (for validation), teacher write only",
    purpose: "Maps 6-char invite codes to courses for enrollment"
  },

  // Course enrollments (teacher view)
  course_enrollments: {
    description: "List of students enrolled in each course",
    path: "course_enrollments/{teacherUserId}/{courseId}/{enrollmentId}",
    structure: {
      studentId: "string",
      studentEmail: "string",
      studentName: "string",
      enrolledAt: "timestamp",
      enrolledVia: "invite|manual|quiz_registration",  // NEW: Added quiz_registration
      inviteCode: "string|null",
      status: "active|removed"
    },
    security: "Teacher (owner) only",
    indexes: ["studentId", "enrolledAt"]
  },

  // Pending student registrations (for email verification)
  pending_registrations: {
    description: "Temporary records for students completing email verification",
    path: "pending_registrations/{verificationCode}",
    structure: {
      email: "string",
      displayName: "string",
      quizInstancePath: "string",      // Full path to quiz instance
      participantId: "string",         // Participant ID in quiz
      courseId: "string|null",
      courseName: "string|null",
      teacherUserId: "string",
      createdAt: "timestamp",
      expiresAt: "timestamp",          // 24h expiry
      status: "pending|verified|expired"
    },
    security: "Public read (for verification), system write only",
    purpose: "Tracks registration until email is verified",
    cleanup: "Expired records auto-deleted after 48 hours"
  }
};

// ============================================
// HELPER FUNCTIONS FOR PATH GENERATION
// ============================================

export const getFirebasePaths = (userId) => ({
  // User paths
  user: () => `users/${userId}`,
  userSettings: () => `users/${userId}/settings`,

  // Course paths
  courses: () => `courses/${userId}`,
  course: (courseId) => `courses/${userId}/${courseId}`,

  // Template paths
  templates: () => `quiz_templates/${userId}`,
  template: (templateId) => `quiz_templates/${userId}/${templateId}`,
  templateQuestions: (templateId) => `quiz_templates/${userId}/${templateId}/questions`,
  templateSettings: (templateId) => `quiz_templates/${userId}/${templateId}/settings`,

  // Instance paths
  instances: () => `quiz_instances/${userId}`,
  instance: (instanceId) => `quiz_instances/${userId}/${instanceId}`,
  instanceParticipants: (instanceId) => `quiz_instances/${userId}/${instanceId}/participants`,
  instanceAnswers: (instanceId) => `quiz_instances/${userId}/${instanceId}/answers`,
  instanceCurrentQuestion: (instanceId) => `quiz_instances/${userId}/${instanceId}/currentQuestion`,
  instanceTimer: (instanceId) => `quiz_instances/${userId}/${instanceId}/timerState`,

  // Session code lookup
  sessionCode: (code) => `session_codes/${code}`,

  // Legacy (backward compatibility)
  legacySession: (sessionId) => `sessions/${sessionId}`,
  
  // LLM Prompt Templates (System-wide and User-specific)
  llmPromptTemplates: () => `llm_prompt_templates`,
  llmPromptTemplate: (templateId) => `llm_prompt_templates/${templateId}`,
  userLlmTemplates: () => `user_llm_templates/${userId}`,
  userLlmTemplate: (templateId) => `user_llm_templates/${userId}/${templateId}`
});

// ============================================
// MIGRATION HELPERS
// ============================================

export const migrationHelpers = {
  // Add userId to existing sessions
  addUserIdToLegacySessions: async (database, userId) => {
    const { ref, update, get } = await import('firebase/database');
    const sessionsRef = ref(database, 'sessions');
    const snapshot = await get(sessionsRef);
    
    if (!snapshot.exists()) return;
    
    const updates = {};
    snapshot.forEach((child) => {
      updates[`sessions/${child.key}/userId`] = userId;
    });
    
    await update(ref(database), updates);
    log.info(`Added userId to ${Object.keys(updates).length} sessions`);
  },

  // Convert legacy session to template
  sessionToTemplate: (session, userId, courseId = null) => {
    return {
      name: session.settings?.displayName || "Untitled Quiz",
      courseId,
      description: "",
      tags: [],
      questions: session.questions || [],
      settings: session.settings || {},
      createdAt: Date.now(),
      lastModified: Date.now(),
      lastUsed: session.createdAt || Date.now(),
      useCount: 1
    };
  },

  // Convert legacy session to instance
  sessionToInstance: (session, userId, templateId, courseId = null) => {
    return {
      templateId,
      courseId,
      name: session.settings?.displayName || "Untitled Session",
      sessionCode: generateSessionCode(),
      status: session.showResults ? "completed" : "active",
      questions: session.questions || [],
      settings: session.settings || {},
      createdAt: session.createdAt || Date.now(),
      startedAt: session.createdAt || Date.now(),
      endedAt: session.showResults ? Date.now() : null,
      currentQuestion: session.currentQuestion || 0,
      showResults: session.showResults || false,
      showCorrectAnswer: session.showCorrectAnswer || false,
      answersLocked: session.answersLocked || false,
      timerState: session.timerState || {},
      participants: session.participants || {},
      answers: session.answers || {}
    };
  }
};

// Generate 6-character session code
function generateSessionCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No ambiguous chars
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

// ============================================
// EXAMPLE USAGE
// ============================================

/*
import { getFirebasePaths } from './firebaseSchema';
import { ref, set, get } from 'firebase/database';

const paths = getFirebasePaths(userId);

// Create a course
await set(ref(database, paths.course(courseId)), {
  name: "CS 101",
  description: "Intro to Programming",
  color: "#3B82F6",
  icon: "💻",
  createdAt: Date.now(),
  archived: false
});

// Create a template
await set(ref(database, paths.template(templateId)), {
  name: "Variables Quiz",
  courseId: courseId,
  questions: [...],
  settings: {...},
  createdAt: Date.now(),
  useCount: 0
});

// Start an instance from template
const templateSnap = await get(ref(database, paths.template(templateId)));
const template = templateSnap.val();
await set(ref(database, paths.instance(instanceId)), {
  templateId: templateId,
  courseId: template.courseId,
  name: template.name + " - Week 3",
  sessionCode: generateSessionCode(),
  status: "active",
  questions: template.questions,
  settings: template.settings,
  createdAt: Date.now(),
  participants: {},
  answers: {}
});
*/
